import { useCallback } from 'react';
import { AxiosError, isAxiosError } from 'axios';
import { useDefaultErrorHandlers } from '@antimatterhq/ui/contexts/DefaultErrorHandlerContext';

/**
 * @description Provides a default error handling function for Axios requests,
 * primarily designed for use within React Query wrappers (`useQuery`, `useMutation`).
 * It checks for common error types (currently AxiosErrors) and delegates specific
 * handling, like 401 Unauthorized errors, to handlers provided by `DefaultErrorHandlerContext`.
 *
 * @returns {function(error: unknown): Promise<boolean>} An asynchronous function (`handleError`)
 * that takes an unknown error. It returns a Promise resolving to `true` if the error
 * (specifically a 401 AxiosError) was successfully handled (e.g., token refresh succeeded),
 * and `false` otherwise. This boolean is crucial for retry logic in query wrappers.
 */
export function useDefaultRequestErrorHandling() {
  const { defaultAxiosErrorHandler } = useDefaultErrorHandlers();

  /**
   * @internal
   * @description Handles specific AxiosError cases. Currently focuses on 401 errors.
   * @param {AxiosError} error - The Axios error object.
   * @returns {Promise<boolean>} A Promise resolving to `true` if the 401 error was successfully handled
   * by the `defaultAxiosErrorHandler` (indicating potential token refresh success), `false` otherwise.
   */
  const handleRequestError = useCallback(
    async (error: AxiosError): Promise<boolean> => {
      const handled = await defaultAxiosErrorHandler(error);
      return handled;
    },
    [defaultAxiosErrorHandler],
  );

  /**
   * @description The main error handler function returned by the hook.
   * It checks if the error is an AxiosError and passes it to `handleRequestError`.
   * @param {unknown} error - The error caught from a request (e.g., in a queryFn or mutationFn).
   * @returns {Promise<boolean>} A Promise resolving to `true` if the error was an AxiosError
   * and was successfully handled by `handleRequestError` (specifically a 401 indicating
   * token refresh success), `false` otherwise.
   */
  const handleError = useCallback(
    async (error: unknown): Promise<boolean> => {
      if (isAxiosError(error)) {
        const handled = await handleRequestError(error);
        return handled;
      }

      return false;
    },
    [handleRequestError],
  );

  return handleError;
}
